import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CreditCard, Mail, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { SocialLinks } from "./SocialLinks";
import { NAV_LINKS, PAYMENT_URL, CONTACT } from "./data";
import { scrollToSection } from "@/lib/scrollTo";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
    } else {
      scrollToSection(href);
    }
  };

  return (
    <motion.header
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.33, 1, 0.68, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-[80] transition-colors duration-500 ${
        scrolled
          ? "border-b border-[#A07E1F]/15 bg-white/92 shadow-[0_4px_24px_rgba(18,41,77,0.08)] backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div
        className={`overflow-hidden border-b border-[#A07E1F]/10 bg-white/70 backdrop-blur-md transition-all duration-500 ${
          scrolled ? "max-h-0 border-b-0" : "max-h-10"
        }`}
        data-testid="topbar"
      >
        <div className="mx-auto flex h-10 max-w-7xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <span className="hidden font-mono-accent text-[9px] uppercase tracking-[0.25em] text-[#75849B] sm:block">
              Connect with us
            </span>
            <SocialLinks testidPrefix="topbar-social" iconSize={13} />
          </div>
          <div className="flex items-center gap-5">
            <a href={`mailto:${CONTACT.email}`} data-testid="topbar-email" className="hidden items-center gap-1.5 font-mono-accent text-[10px] tracking-[0.08em] text-[#5E6E85] transition-colors hover:text-[#A07E1F] md:flex">
              <Mail size={12} className="text-[#A07E1F]" /> {CONTACT.email}
            </a>
            <a href="tel:+442073626786" data-testid="topbar-phone" className="flex items-center gap-1.5 font-mono-accent text-[10px] tracking-[0.08em] text-[#5E6E85] transition-colors hover:text-[#A07E1F]">
              <Phone size={12} className="text-[#A07E1F]" /> {CONTACT.phone}
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button onClick={() => go("#top")} className="focus:outline-none" data-testid="nav-logo-button" aria-label="Back to top">
          <Logo />
        </button>

        <nav className="hidden items-center gap-6 lg:flex" data-testid="nav-links">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative font-mono-accent text-[11px] uppercase tracking-[0.16em] text-[#46586F] transition-colors duration-300 hover:text-[#12294D]"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#D4AF37] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="group relative font-mono-accent text-[11px] uppercase tracking-[0.16em] text-[#46586F] transition-colors duration-300 hover:text-[#12294D]"
          >
            Blog
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#D4AF37] transition-[width] duration-300 group-hover:w-full" />
          </Link>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={PAYMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-pay-now-button"
            className="flex items-center gap-2 rounded-full border border-[#A07E1F]/50 px-5 py-2.5 font-mono-accent text-[11px] uppercase tracking-[0.16em] text-[#A07E1F] transition-colors duration-300 hover:bg-[#D4AF37]/15"
          >
            <CreditCard size={14} /> Pay Now
          </a>
          <Link
            to="/order"
            data-testid="nav-order-now-button"
            className="whitespace-nowrap rounded-full bg-[#D4AF37] px-6 py-2.5 font-mono-accent text-[11px] font-bold uppercase tracking-[0.16em] text-[#12294D] transition-colors duration-300 hover:bg-[#C9A227]"
          >
            Order Now
          </Link>
        </div>

        <button
          className="text-[#12294D] lg:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="overflow-hidden border-b border-[#A07E1F]/15 bg-white/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="py-3 text-left font-serif-display text-2xl text-[#12294D] transition-colors hover:text-[#A07E1F]"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/blog"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-blog"
                className="py-3 text-left font-serif-display text-2xl text-[#12294D] transition-colors hover:text-[#A07E1F]"
              >
                Blog
              </Link>
              <Link
                to="/why-choose-us"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-link-why-us"
                className="py-3 text-left font-serif-display text-2xl text-[#12294D] transition-colors hover:text-[#A07E1F]"
              >
                Why Choose Us
              </Link>
              <div className="mt-4 flex gap-3">
                <a
                  href={PAYMENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="nav-mobile-pay-now-button"
                  className="flex-1 rounded-full border border-[#A07E1F]/50 py-3 text-center font-mono-accent text-[11px] uppercase tracking-[0.16em] text-[#A07E1F]"
                >
                  Pay Now
                </a>
                <Link
                  to="/order"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-order-now-button"
                  className="flex-1 rounded-full bg-[#D4AF37] py-3 text-center font-mono-accent text-[11px] font-bold uppercase tracking-[0.16em] text-[#12294D]"
                >
                  Order Now
                </Link>
              </div>
              <div className="mt-5 flex justify-center">
                <SocialLinks testidPrefix="mobile-social" dark />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
